/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import PropTypes from "prop-types";
import { BrowserRouter as Router, Route, Switch, Redirect, Link } from "react-router-dom";
import { ProSidebarProvider } from "react-pro-sidebar";
import PrimaryNavigation from "../PrimaryNavigation/PrimaryNavigation";
import Home from "../Home/Home";
import PromptsManager from "../../modules/promptsManager/Module";
import WebChatManager from "../../modules/webChatManager/Module";
import LexBotManager from "../../modules/lexBotManager/Module";
import Handling from "../../modules/dispositionManager/Module";
import SettingsManager from "../../modules/settingsManager/Module";
import AgentConsole from "../../modules/agentConsole/Module";
import DashboardManager from "../../modules/dashboardManager/Module";
import { getLoggedInUserData } from "../../utility/auth";
import "./App.scss";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../utility/errorCodes";
import Connect from "../../utility/connect-endpoints";
import { DashboardUrlConfigsProvider, useDashboardUrlConfigs } from "../../modules/dashboardManager/context/Provider";
import { PERMISSIONS, userHasPermission, getEnableAllPermissionId } from "../../utility/permissions";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateUtils from "@date-io/date-fns";
import SidebarNav from "../ModulesNavigation/SidebarNav";
import { Box, CssBaseline, Toolbar } from "@material-ui/core";
import ContentHeader from "../ModulesNavigation/ContentHeader";

const COMPONENT_NAME = "Admin components/App";

const modules = ({ enableDashboards, dashboardUrls }) => [
	{
		id: "AgentConsole",
		title: "Agent Console",
		iconClass: "fas fa-phone-alt",
		path: "/agentConsole",
		subpaths: [
			{
				id: PERMISSIONS.AGENT_CONSOLE_PAGE,
				title: "Agent Console",
				path: "/agentConsole",
				hidden: false,
				externalUrl: `${window.location.origin}/agentConsole`,
				pageStyleVariables: {
					"--background": "#fff",
					"--header-height": "60px",
					"--module-content-padding": "0 15px 25px 25px",
					"--module-content-margin": "0px",
				},
				hideNavBars: true,
			},
			{
				id: PERMISSIONS.UPDATE_CONTACT_PAGE,
				title: "Update Contact",
				path: "/updateContact",
				pageTitle: "Update Contact",
				pageDescription: "Find and update the dispositions and notes for a Contact ID",
			},
		],
		component: <AgentConsole />,
		settings: [],
	},
	{
		...prepareDashboardsNav(enableDashboards, dashboardUrls, {
			id: "Dashboards",
			title: "Dashboards",
			iconClass: "fa fa-chart-bar",
			path: "/dashboards",
			subpaths: [],
			component: <DashboardManager />,
			settings: [],
		}),
	},
	{
		id: "PromptsManagement",
		title: "Prompts Management",
		iconClass: "fas fa-microphone-alt",
		path: "/prompts",
		subpaths: [
			{
				id: PERMISSIONS.PROMPTS_MANAGEMENT_CONFIG_PAGE,
				title: "Configuration",
				path: "/configuration",
			},
			{
				id: PERMISSIONS.PROMPTS_MANAGEMENT_PROMPTS_PAGE,
				title: "Prompts",
				pageTitle: "Prompts",
				pageDescription: "Manage a list of prompts that can be referenced from contact flows.",
				path: "/",
			},
		],
		component: <PromptsManager />,
		settings: [
			{
				id: "promptsSettings1",
				title: "Name",
				type: "string",
				default: "Prompts Management",
				value: null,
			},
			{
				id: "promptsSettings2",
				title: "Prompts Info Endpoint",
				type: "string",
				default: "https://xxxxxxxxxx.execute-api.us-east-1.amazonaws.com/Stage/promptsInfo",
				value: null,
			},
			{
				id: "promptsSettings3",
				title: "Prompt Types",
				type: "string",
				default: "TTS,SSML,audio",
				value: null,
			},
			{
				id: "promptsSettings4",
				title: "Language Max Length",
				type: "string",
				default: "2",
				value: null,
			},
		],
	},
	{
		id: "Handling",
		title: "Handling",
		iconClass: "fas fa-tags",
		path: "/handling",
		subpaths: [
			{
				id: PERMISSIONS.CANNED_RESPONSES_PAGE,
				title: "Canned Responses",
				path: "/CannedResponses",
				pageTitle: "Canned Responses",
				pageDescription: "Pre-Configured responses to be used by agents during chat conversations.",
			},
			{
				id: PERMISSIONS.DISPOSITION_MANAGEMENT_PAGE,
				title: "Disposition Management",
				path: "/",
				hidden: true,
				pageTitle: "Disposition Configuration",
				pageDescription:
					"Configure dispositions and sub-dispositions to be used by agents to categorize a customer contact during or after a chat or call.",
			},
			{
				id: PERMISSIONS.DISPOSITION_MANAGEMENT_PAGE,
				title: "Disposition Management",
				path: "/DispositionManagement",
				pageTitle: "Disposition Configuration",
				pageDescription:
					"Configure dispositions and sub-dispositions to be used by agents to categorize a customer contact during or after a chat or call.",
			},
			{
				id: PERMISSIONS.EXTERNAL_CONTACTS_PAGE,
				title: "External Contacts",
				path: "/externalContacts",
				pageTitle: "External Contacts",
				pageDescription: (
					<span className="text-muted">
						Manage a list of contacts that can be used for external contacts on the Agent Console. You can optionally
						tag contacts for easier searching, and enable Microsoft Teams status display (
						<Link to="/settings/integrations?tab=Teams">Microsoft Teams Integration</Link> has to be configured to use
						this feature).
					</span>
				),
			},
			{
				id: PERMISSIONS.STATUS_ALERTS_PAGE,
				title: "Status Alerts",
				path: "/statusAlerts",
				pageTitle: "Status Alerts Configuration",
				pageDescription: "Create status reminders for agents to switch to Available from non-routable statuses.",
			},
		],
		component: <Handling />,
		settings: [],
	},
	{
		id: "WidgetManagement",
		title: "Widget Management",
		iconClass: "fas fa-code-branch",
		path: "/widgetManagement",
		subpaths: [
			{
				id: PERMISSIONS.CALL_ME_NOW_PAGE,
				title: "Call Me Now",
				path: "/callMeNow",
				pageTitle: "Call Me Now Configuration",
				pageDescription:
					"Allows you to customize and deploy a widget that allows customers to request a call directly to their phone.",
			},
			{
				id: PERMISSIONS.TRANSLATION_TERMINOLOGY_PAGE,
				title: "Translation Terminology",
				path: "/translationTerminology",
				pageTitle: "Translation Terminology",
				pageDescription:
					"Allows to enter words that shouldn't be automatically translated when interacting with customers who speak other languages.",
			},
			{
				id: PERMISSIONS.WEB_CHAT_PAGE,
				title: "Web Chat",
				path: "/webChat",
				pageTitle: "Web Chat Configuration",
				pageDescription: "Manage web chat configurations and surveys.",
			},
			{
				id: PERMISSIONS.WEB_CHAT_PAGE,
				title: "Web Chat",
				path: "/",
				hidden: true,
			},
		],
		component: <WebChatManager />,
		settings: [],
	},
	{
		id: "LexBotManagement",
		title: "Lex Bot Management",
		iconClass: "fas fa-robot",
		path: "/lexBotManagement",
		subpaths: [
			{
				id: PERMISSIONS.LEX_BOTS_PAGE,
				title: "Lex Bots",
				path: "/lexBots",
				pageTitle: "Lex Bots",
				pageDescription: (
					<span className="text-muted">
						Manage {"  "}
						<a
							href={`https://console.aws.amazon.com/lexv2/home?region=${process.env.REACT_APP_AWS_REGION}#bots`}
							target={"_blank"}
							rel="noreferrer"
						>
							Amazon Lex
						</a>
						{" bots Version 2."}
					</span>
				),
			},
			{
				id: PERMISSIONS.LEX_BOTS_PAGE,
				title: "Lex Bots",
				path: "/",
				hidden: true,
				pageTitle: "Lex Bots",
				pageDescription: (
					<span className="text-muted">
						Manage {"  "}
						<a
							href={`https://console.aws.amazon.com/lexv2/home?region=${process.env.REACT_APP_AWS_REGION}#bots`}
							target={"_blank"}
							rel="noreferrer"
						>
							Amazon Lex
						</a>
						{" bots Version 2."}
					</span>
				),
			},
		],
		component: <LexBotManager />,
		settings: [],
	},
	{
		id: "Settings",
		title: "Settings",
		iconClass: "fas fa-cog",
		path: "/settings",
		subpaths: [
			{
				id: PERMISSIONS.SETTINGS_AGENT_CONSOLE_PAGE,
				title: "Agent Console",
				path: "/agentConsole",
			},
			{
				id: PERMISSIONS.AGENT_EXTENSIONS_PAGE,
				title: "Agent Extensions",
				path: "/agentExtensions",
				pageTitle: "Agent Extensions",
				pageDescription: "Manage a list of agent extensions and voicemail configurations.",
			},
			{
				id: PERMISSIONS.INTEGRATIONS_PAGE,
				title: "Integrations",
				path: "/integrations",
				pageTitle: "Integrations",
			},
			{
				id: PERMISSIONS.QUEUES_PAGE,
				title: "Queue Management",
				path: "/queues",
				pageTitle: "Queue Management",
				pageDescription: "Configure and modify queue closures and closure prompts, holidays, and callbacks.",
			},
			{
				id: PERMISSIONS.REPORTING_CONFIGURATION_PAGE,
				title: "Reporting Configuration",
				path: "/reportingConfiguration",
			},
			{
				id: PERMISSIONS.USER_MANAGEMENT_PAGE,
				title: "User Management",
				path: "/userManagement",
				pageTitle: "User Management",
			},

			{
				id: PERMISSIONS.SETTINGS_AGENT_CONSOLE_PAGE,
				title: "Agent Console",
				path: "/",
				hidden: true,
			},
		],
		component: <SettingsManager />,
		settings: [],
	},
	{
		id: "AmazonConsole",
		title: "Amazon Connect",
		icon: `${process.env.PUBLIC_URL}/images/icons/AWS-amazon-connect.png`,
		externalUrl: `${Connect.connectConsole}/home`,
		newTab: true,
	},
];

const prepareDashboardsNav = (enableDashboards, dashboardUrls, existingConfig) => {
	const navConfig = { ...existingConfig };
	if (!enableDashboards) {
		navConfig.hidden = true;
	} else if (Array.isArray(dashboardUrls)) {
		if (dashboardUrls.length === 0) {
			navConfig.path = "#";
			navConfig.subpaths = [{ title: "No dashboards configured", path: "" }];
		} else {
			navConfig.subpaths = [];
			for (let urlIdx = 0; urlIdx < dashboardUrls.length; urlIdx++) {
				const url = dashboardUrls[urlIdx];
				navConfig.subpaths.push({
					id: url.reportId,
					title: url.reportName,
					hideBreadCrumbs: true,
					hidden: url.validConfig,
					pageStyleVariables: {
						"--module-content-padding": "0px",
						"--module-content-margin": "0px",
					},
					path: `/${url.reportId}`,
				});
			}
		}
	}
	return navConfig;
};

const App = ({ signOut, licenseResult }) => {
	const [userInfo, setUserInfo] = React.useState({ email: "", roles: [], userId: undefined });
	const [initialized, setInitialized] = React.useState(false);
	const { enableDashboards, filteredDashboardUrls: dashboardUrls } = useDashboardUrlConfigs();
	let sidebarModules = modules({ enableDashboards, dashboardUrls });

	React.useEffect(() => {
		getLoggedInUserData()
			.then(({ email, roles, userId }) => {
				setUserInfo({ email, roles, userId });
				setInitialized(true);
			})
			.catch((error) =>
				v2.error(`Error getting logged in user`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_USER_DATA_ERROR,
				}),
			);
	}, []);

	let modulesList = [];
	sidebarModules.forEach((module) => {
		let subpaths = [];
		const enableAllPermissionId = getEnableAllPermissionId(module.id);
		module.subpaths?.forEach((subpath) => {
			if (!subpath.hidden && (userHasPermission(enableAllPermissionId) || userHasPermission(subpath.id))) {
				subpaths.push(subpath);
			}
		});
		if (subpaths.length || module.externalUrl) {
			modulesList.push({ ...module, subpaths });
		}
	});

	sidebarModules = modulesList;
	const isUserLicenseValid = licenseResult === "Success" || licenseResult === "Warning";
	return (
		<Router>
			<>
				{initialized && (
					<Box className="d-flex" style={{ flex: 1 }}>
						<CssBaseline />
						<PrimaryNavigation signOut={signOut} userInfo={userInfo} />
						{isUserLicenseValid && <SidebarNav modules={sidebarModules} />}
						<Box component="main" className="flex-grow-1 px-4">
							<Toolbar /> {/* leaving top toolbar space */}
							<ContentHeader licenseResult={licenseResult} modules={sidebarModules} />
							{isUserLicenseValid && (
								<Switch>
									<Route
										path="/(dashboard|)"
										render={(props) => {
											return <Home {...props} />;
										}}
									></Route>
									<Route path="/oauth-callback">
										<Redirect
											to={{
												pathname: "/dashboard",
												state: { redirectReason: "New Login" },
											}}
										/>
									</Route>
									{sidebarModules
										.filter((m) => m.component)
										.map((m) => (
											<Route key={m.id} path={m.path}>
												<div className="module-content">{m.component}</div>
											</Route>
										))}
									<Route>
										<Redirect
											to={{
												pathname: "/dashboard",
												state: { redirectReason: "Unauthorized" },
											}}
										/>
									</Route>
								</Switch>
							)}
						</Box>
					</Box>
				)}
			</>
		</Router>
	);
};

App.propTypes = {
	signOut: PropTypes.func.isRequired,
};

const AppContainer = (props) => (
	<ProSidebarProvider>
		<DashboardUrlConfigsProvider>
			<MuiPickersUtilsProvider utils={DateUtils}>
				<App {...props} />
			</MuiPickersUtilsProvider>
		</DashboardUrlConfigsProvider>
	</ProSidebarProvider>
);

export default AppContainer;
